// src/components/ClientPortalInvoices.js

import React, { useCallback, useState, useEffect } from 'react';
import 'animate.css';

function ClientPortalInvoices({ currentUser, clients, invoices, invoiceItems, authAxios }) {
  // Find the specific client profile linked to the current user
  const clientProfile = clients.find(client => client.user === currentUser.id);

  // Filter invoices relevant to this specific client profile
  const clientInvoices = invoices.filter(i => i.client === (clientProfile ? clientProfile.id : null));

  // State for expanded invoice details
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [expenseEntries, setExpenseEntries] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch time and expense entries for invoice breakdown
  const fetchInvoiceDetails = useCallback(async (invoiceId) => {
    if (!authAxios) return;
    setLoadingDetails(true);
    try {
      const [timeResp, expenseResp] = await Promise.all([
        authAxios.get('/time-entries/', { params: { invoice: invoiceId } }),
        authAxios.get('/expense-entries/', { params: { invoice: invoiceId } })
      ]);
      setTimeEntries(timeResp.data?.results || timeResp.data || []);
      setExpenseEntries(expenseResp.data?.results || expenseResp.data || []);
    } catch (err) {
      console.error('Failed to fetch invoice details', err);
    } finally {
      setLoadingDetails(false);
    }
  }, [authAxios]);

  // Toggle invoice expansion
  const toggleInvoiceExpansion = (invoiceId) => {
    if (expandedInvoice === invoiceId) {
      setExpandedInvoice(null);
      setTimeEntries([]);
      setExpenseEntries([]);
    } else {
      setExpandedInvoice(invoiceId);
      fetchInvoiceDetails(invoiceId);
    }
  };

  // Calculate total amount for a given invoice (for display)
  const calculateInvoiceTotal = useCallback((invoiceId) => {
    return invoiceItems
      .filter(item => item.invoice === invoiceId)
      .reduce((total, item) => total + (parseFloat(item.quantity) * parseFloat(item.unit_price)), 0)
      .toFixed(2);
  }, [invoiceItems]);

  // Check if invoice is outstanding
  const isOutstanding = (invoice) => {
    return ['Sent', 'Overdue'].includes(invoice.status);
  };

  // Get balance due
  const getBalanceDue = (invoice) => {
    return invoice.balance_due !== undefined 
      ? parseFloat(invoice.balance_due)
      : parseFloat(calculateInvoiceTotal(invoice.id));
  };

  return (
    <div className="client-portal-invoices animate__animated animate__fadeInUp p-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Your Invoices</h2>
      {clientProfile ? (
        <p className="text-gray-600 mb-6">View your invoices and billing details, {clientProfile.first_name} {clientProfile.last_name}.</p>
      ) : (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <p className="text-yellow-700">Your user account is not yet linked to a client profile. Please contact the firm administrator to view your invoices.</p>
        </div>
      )}

      {/* Outstanding Invoices Section */}
      {clientInvoices.filter(inv => isOutstanding(inv)).length > 0 && (
        <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-red-800">Outstanding Invoices</h3>
            <div className="text-2xl font-bold text-red-600">
              P{clientInvoices
                .filter(inv => isOutstanding(inv))
                .reduce((sum, inv) => sum + getBalanceDue(inv), 0)
                .toFixed(2)}
            </div>
          </div>
          <div className="space-y-4">
            {clientInvoices.filter(inv => isOutstanding(inv)).map(invoice => (
              <div key={invoice.id} className="bg-white rounded-lg shadow-sm border border-red-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <div className="font-semibold text-lg text-gray-800">
                      Invoice #{invoice.invoice_number || invoice.id}
                    </div>
                    <div className="text-sm text-gray-600">
                      {invoice.case_title || 'General Services'}
                    </div>
                    <div className="flex gap-4 mt-2 text-sm">
                      <span className="text-gray-600">
                        <i className="bi bi-calendar mr-1"></i>
                        Due: {new Date(invoice.due_date).toLocaleDateString()}
                      </span>
                      <span className={`font-medium ${invoice.status === 'Overdue' ? 'text-red-600' : 'text-yellow-600'}`}>
                        {invoice.status}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-800">
                      P{getBalanceDue(invoice).toFixed(2)}
                    </div>
                    <button className="mt-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2">
                      <i className="bi bi-credit-card"></i>
                      Pay Now
                    </button>
                  </div>
                </div>

                {/* View Details Button */}
                <button
                  onClick={() => toggleInvoiceExpansion(invoice.id)}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                >
                  <i className={`bi ${expandedInvoice === invoice.id ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  {expandedInvoice === invoice.id ? 'Hide' : 'View'} Itemized Breakdown
                </button>

                {/* Expanded Details */}
                {expandedInvoice === invoice.id && (
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    {loadingDetails ? (
                      <div className="text-center py-4 text-gray-500">Loading details...</div>
                    ) : (
                      <div className="space-y-4">
                        {/* Invoice Items */}
                        {invoiceItems.filter(item => item.invoice === invoice.id).length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-700 mb-2">Invoice Items</h4>
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left">Description</th>
                                  <th className="px-3 py-2 text-right">Quantity</th>
                                  <th className="px-3 py-2 text-right">Rate</th>
                                  <th className="px-3 py-2 text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {invoiceItems.filter(item => item.invoice === invoice.id).map(item => (
                                  <tr key={item.id} className="border-t">
                                    <td className="px-3 py-2">
                                      {item.service_product_name || 'Custom Item'}
                                      {item.description && (
                                        <div className="text-xs text-gray-500">{item.description}</div>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-right">{item.quantity}</td>
                                    <td className="px-3 py-2 text-right">P{parseFloat(item.unit_price).toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-medium">
                                      P{(parseFloat(item.quantity) * parseFloat(item.unit_price)).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Time Entries */}
                        {timeEntries.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-700 mb-2">Time Entries</h4>
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left">Date</th>
                                  <th className="px-3 py-2 text-left">Description</th>
                                  <th className="px-3 py-2 text-right">Hours</th>
                                  <th className="px-3 py-2 text-right">Rate</th>
                                  <th className="px-3 py-2 text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {timeEntries.map(entry => (
                                  <tr key={entry.id} className="border-t">
                                    <td className="px-3 py-2">{new Date(entry.date).toLocaleDateString()}</td>
                                    <td className="px-3 py-2">
                                      {entry.description || 'Legal services'}
                                      <div className="text-xs text-gray-500">{entry.user_name}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right">{entry.duration_hours}</td>
                                    <td className="px-3 py-2 text-right">
                                      P{(entry.service_product_price || 0).toFixed(2)}
                                    </td>
                                    <td className="px-3 py-2 text-right font-medium">
                                      P{((entry.duration_hours || 0) * (entry.service_product_price || 0)).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Disbursements */}
                        {expenseEntries.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-700 mb-2">Expenses</h4>
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left">Date</th>
                                  <th className="px-3 py-2 text-left">Category</th>
                                  <th className="px-3 py-2 text-left">Description</th>
                                  <th className="px-3 py-2 text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {expenseEntries.map(entry => (
                                  <tr key={entry.id} className="border-t">
                                    <td className="px-3 py-2">{new Date(entry.date).toLocaleDateString()}</td>
                                    <td className="px-3 py-2">{entry.category || 'General'}</td>
                                    <td className="px-3 py-2">{entry.description || 'Expense'}</td>
                                    <td className="px-3 py-2 text-right font-medium">
                                      P{parseFloat(entry.amount || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Invoices Section */}
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">All Invoices</h3>
        {clientInvoices.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Invoice #</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Case</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Due Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Amount</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {clientInvoices.map(invoice => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">
                      {invoice.invoice_number || invoice.id}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{invoice.case_title || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(invoice.issue_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(invoice.due_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          invoice.status === 'Paid'
                            ? 'bg-green-100 text-green-700'
                            : invoice.status === 'Overdue'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-right text-gray-800">
                      P{calculateInvoiceTotal(invoice.id)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <button
                        onClick={() => toggleInvoiceExpansion(invoice.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <i className="bi bi-eye"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
            <i className="bi bi-inbox text-4xl text-blue-400 mb-2"></i>
            <p className="text-blue-700">No invoices found associated with your client profile.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientPortalInvoices;
